// ?invite=<token>: POST auth/invitations/accept {token, displayName,
// password} (password min 14). Success reports the workspace and role and
// whether email verification is still required; then the door.
import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { postInvitationAccept } from '../../api/endpoints';
import { isApiError } from '../../api/errors';
import { useElapsedLine } from '../../states/useElapsed';
import { HonestState } from '../../states/HonestState';
import { stateOfError } from '../../states/stateOf';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Input';
import { AuthFrame } from './AuthFrame';

export const PASSWORD_MIN_LENGTH = 14;

export interface InvitationAcceptProps {
  readonly token: string;
  readonly onDone: () => void;
}

export function InvitationAccept({ token, onDone }: InvitationAcceptProps) {
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const accept = useMutation({ mutationFn: postInvitationAccept });
  const elapsed = useElapsedLine(accept.isPending);
  const tooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!displayName.trim() || password.length < PASSWORD_MIN_LENGTH) return;
    accept.mutate({ token, displayName: displayName.trim(), password });
  };

  if (accept.isSuccess) {
    const data = accept.data;
    return (
      <AuthFrame title="Invitation accepted" where="Invitation">
        <HonestState
          variant="Ready"
          title={`Member of ${data.tenant.name} as ${data.role}`}
          reason={data.verificationRequired
            ? `A verification email was sent${data.email ? ` to ${data.email}` : ''}. Open its link, then sign in.`
            : 'Your account is ready. Sign in with the email or Discord identity on the invitation.'}
          nextAction={{ label: 'Go to sign in', onSelect: onDone }}
        />
      </AuthFrame>
    );
  }

  const failure = accept.isError ? stateOfError(accept.error) : null;
  const invalidToken = accept.isError && isApiError(accept.error) && (accept.error.status === 400 || accept.error.status === 404 || accept.error.status === 410);

  return (
    <AuthFrame title="Accept your invitation" where="Invitation" lede="Choose the name other members will see and a password of at least 14 characters. The invitation token came with the link you opened.">
      <form className="door-form" onSubmit={onSubmit} aria-label="Accept invitation">
        <label className="door-lbl" htmlFor="inv-name">Display name</label>
        <Field id="inv-name" name="displayName" autoComplete="nickname" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required maxLength={64} />
        <label className="door-lbl" htmlFor="inv-password">Password, 14 characters or more</label>
        <Field id="inv-password" name="password" type="password" revealable autoComplete="new-password" value={password} invalid={tooShort} onChange={(event) => setPassword(event.target.value)} required minLength={PASSWORD_MIN_LENGTH} />
        {tooShort ? <p className="door-note">{PASSWORD_MIN_LENGTH - password.length} more characters needed.</p> : null}
        <div className="door-row">
          <Button variant="primary" type="submit" disabled={accept.isPending}>{accept.isPending ? 'Accepting' : 'Accept invitation'}</Button>
          <Button variant="tertiary" onClick={onDone}>Back to sign in</Button>
        </div>
        {accept.isPending ? <HonestState variant="Loading" reason="Waiting for the invitation answer (15 s limit)." detail={elapsed} nextAction={{ label: 'Cancel', onSelect: () => accept.reset() }} /> : null}
        {failure ? (
          <HonestState
            variant={invalidToken ? 'Permission denied' : failure.variant}
            title={invalidToken ? 'Invitation not accepted' : 'Invitation could not be accepted'}
            reason={invalidToken ? 'The invitation token was rejected: it may be expired, revoked, or already used. Ask the owner for a new link.' : failure.reason}
            nextAction={[{ label: 'Try again', onSelect: () => accept.reset() }, { label: 'Back to sign in', onSelect: onDone }]}
          />
        ) : null}
      </form>
    </AuthFrame>
  );
}
